import { API_TIMEOUT, getFullURL } from './apiConfig';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  token?: string | null;
  timeout?: number;
}

interface ApiResponse<T> {
  data: T;
  status: number;
}

class ApiError extends Error {
  status: number;
  code?: string;
  details?: string[];
  constructor(message: string, status: number, details?: string[], code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const createTimeoutController = (timeout: number) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
};

function parseJsonResponse<T>(responseText: string, status: number): T {
  try {
    return JSON.parse(responseText) as T;
  } catch {
    throw new ApiError('服务返回了无效响应，请稍后重试', status);
  }
}

const request = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    method = 'GET',
    headers = {},
    body,
    token,
    timeout = API_TIMEOUT,
  } = options;

  const { controller, timeoutId } = createTimeoutController(timeout);
  const url = getFullURL(endpoint);

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    signal: controller.signal,
    // Project pipeline reads must reflect the latest storyboard save when the
    // user moves between stages; cached GET responses can otherwise restore
    // the previous frame set from the browser cache.
    cache: method === 'GET' ? 'no-store' : undefined,
  };

  if (body !== undefined && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);
    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage = '请求失败';
      let errorDetails: string[] | undefined;
      let errorCode: string | undefined;
      try {
        const errorData = parseJsonResponse<{
          code?: unknown;
          error?: unknown;
          message?: unknown;
          details?: unknown;
        }>(responseText, response.status);
        errorCode = typeof errorData.code === 'string' ? errorData.code : undefined;
        errorMessage = errorCode === 'INSUFFICIENT_POINTS'
          ? '积分不足'
          : typeof errorData.error === 'string'
            ? errorData.error
            : typeof errorData.message === 'string'
              ? errorData.message
              : errorMessage;
        if (Array.isArray(errorData.details)) {
          errorDetails = errorData.details.filter((detail): detail is string => typeof detail === 'string');
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      if (response.status === 401 && token) {
        localStorage.removeItem('auth-storage');
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }

      throw new ApiError(errorMessage, response.status, errorDetails, errorCode);
    }

    const data = response.status === 204
      ? undefined as T
      : parseJsonResponse<T>(responseText, response.status);
    return {
      data,
      status: response.status,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiError('请求超时', 408);
      }
      throw new ApiError(error.message || '网络错误', 0);
    }
    throw new ApiError('未知错误', 0);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const apiClient = {
  get: <T>(endpoint: string, token?: string | null) =>
    request<T>(endpoint, { method: 'GET', token }),

  post: <T>(
    endpoint: string,
    body?: unknown,
    token?: string | null,
    timeout?: number,
  ) => request<T>(endpoint, { method: 'POST', body, token, timeout }),

  postWithHeaders: <T>(
    endpoint: string,
    body: unknown,
    token?: string | null,
    headers?: Record<string, string>,
    timeout?: number,
  ) => request<T>(endpoint, { method: 'POST', body, token, headers, timeout }),

  put: <T>(endpoint: string, body?: unknown, token?: string | null) =>
    request<T>(endpoint, { method: 'PUT', body, token }),

  patch: <T>(endpoint: string, body?: unknown, token?: string | null) =>
    request<T>(endpoint, { method: 'PATCH', body, token }),

  delete: <T>(endpoint: string, token?: string | null) =>
    request<T>(endpoint, { method: 'DELETE', token }),

  upload: async <T>(endpoint: string, formData: FormData, token?: string | null): Promise<T> => {
    const url = getFullURL(endpoint);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage = '上传失败';
      let errorCode: string | undefined;
      try {
        const errorData = parseJsonResponse<{
          code?: unknown;
          error?: unknown;
          message?: unknown;
        }>(responseText, response.status);
        errorCode = typeof errorData.code === 'string' ? errorData.code : undefined;
        errorMessage = errorCode === 'INSUFFICIENT_POINTS'
          ? '积分不足'
          : typeof errorData.error === 'string'
            ? errorData.error
            : typeof errorData.message === 'string'
              ? errorData.message
              : errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new ApiError(errorMessage, response.status, undefined, errorCode);
    }

    return parseJsonResponse<T>(responseText, response.status);
  },
};

export { ApiError, request };
export type { ApiResponse, RequestOptions };
